import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#7c2d12',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#fed7aa',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#ea580c',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#9a3412',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const MobileCateringGasSafetyRecordPDF = ({ formData = {} }) => {
  const eng = formData.gasEngineerDetails || {};
  const owner = formData.vehicleOwnerDetails || {};
  const lpg = formData.lpgStorageCompartment || {};
  const struct = formData.vehicleStructureAndVentilation || {};
  const app = formData.appliances || {};
  const sign = formData.safetyNoticeAndSignOff || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Mobile Catering Vehicle Gas Safety Record</Text>
            <Text style={styles.headerSubtitle}>UKLPG Code of Practice 24 & BS 6173 Catering Installation Compliance</Text>
          </View>
          <View style={styles.badge}>
            <Text>GAS SAFE REGISTER</Text>
          </View>
        </View>

        {/* 1 & 2: Gas Safe Engineer & Vehicle Operator */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Gas Safe Registered Engineer</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Engineer Name:</Text>
              <Text style={styles.fieldValue}>{eng.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Gas Safe ID / Card:</Text>
              <Text style={styles.fieldValue}>{eng.gasSafeId || 'N/A'} (Card: {eng.engineerLicenceCardNo || 'N/A'})</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company:</Text>
              <Text style={styles.fieldValue}>{eng.companyName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Telephone:</Text>
              <Text style={styles.fieldValue}>{eng.telephone || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Vehicle & Operator Identity</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Trading Name:</Text>
              <Text style={styles.fieldValue}>{owner.businessTradingName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Operator Name:</Text>
              <Text style={styles.fieldValue}>{owner.operatorName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Vehicle Reg / VIN:</Text>
              <Text style={styles.fieldValue}>{owner.vehicleRegOrVin || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Description:</Text>
              <Text style={styles.fieldValue}>{owner.vehicleMakeModel || 'Trailer'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date of Test:</Text>
              <Text style={styles.fieldValue}>{owner.inspectionDate || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* 3: LPG Storage & Supply */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. LPG Cylinder Storage & Supply Safety (UKLPG COP 24)</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Locker Location:</Text>
                <Text style={styles.fieldValue}>{lpg.cylinderStorageType || 'External Locker'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Ventilation:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{lpg.compartmentVentilation || 'Satisfactory'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Cylinder Restraints:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{lpg.cylinderRestraints || 'Yes - Strapped'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Regulator & OPSO:</Text>
                <Text style={styles.fieldValue}>{lpg.changeoverRegulatorType || 'Auto Changeover 37mbar'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Pigtails & Expiry:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{lpg.pigtailHoseCondition || 'Pass'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Emergency Shut-off:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{lpg.emergencyIsolationValveAccessible || 'Yes - Labelled'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4: Structure, Ventilation & Tightness */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>4. Vehicle Fire Protection, Extraction & Tightness Testing</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Fire Resisting Lining:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{struct.fireLiningSatisfactory || 'Satisfactory'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Canopy & Air Flow:</Text>
                <Text style={styles.fieldValue}>{struct.canopyExtractionOperational || 'Mechanical'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>CO Alarm Fitted:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{struct.coAlarmFitted || 'Yes - Tested'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Gas Solenoid Interlock:</Text>
                <Text style={styles.fieldValue}>{struct.gasInterlockFitted || 'Verified'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Gas Tightness Test:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{struct.gasTightnessTestPass || 'Pass (0.0 mbar drop)'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Pressure Drop:</Text>
                <Text style={styles.fieldValue}>{struct.testPressureDropMbar || 0} mbar / 2 mins</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 5: Catering Appliances */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>5. Commercial Catering Appliance Checklist (Flame Failure Devices)</Text>
          <View style={styles.fieldRow}>
            <Text style={[styles.fieldLabel, { width: '40%' }]}>Appliance Description</Text>
            <Text style={[styles.fieldLabel, { width: '30%' }]}>Flame Failure (FFD)</Text>
            <Text style={[styles.fieldLabel, { width: '30%' }]}>Visual & Restraint</Text>
          </View>
          {app.appliance1 && (
            <View style={styles.fieldRow}>
              <Text style={[styles.fieldValue, { width: '40%' }]}>1. {app.appliance1}</Text>
              <Text style={[styles.fieldValue, styles.passText, { width: '30%' }]}>{app.appliance1Ffd || 'Pass'}</Text>
              <Text style={[styles.fieldValue, { width: '30%' }]}>{app.appliance1Visual || 'Pass'}</Text>
            </View>
          )}
          {app.appliance2 && (
            <View style={styles.fieldRow}>
              <Text style={[styles.fieldValue, { width: '40%' }]}>2. {app.appliance2}</Text>
              <Text style={[styles.fieldValue, styles.passText, { width: '30%' }]}>{app.appliance2Ffd || 'Pass'}</Text>
              <Text style={[styles.fieldValue, { width: '30%' }]}>{app.appliance2Visual || 'Pass'}</Text>
            </View>
          )}
          {app.appliance3 && (
            <View style={styles.fieldRow}>
              <Text style={[styles.fieldValue, { width: '40%' }]}>3. {app.appliance3}</Text>
              <Text style={[styles.fieldValue, styles.passText, { width: '30%' }]}>{app.appliance3Ffd || 'Pass'}</Text>
              <Text style={[styles.fieldValue, { width: '30%' }]}>{app.appliance3Visual || 'Pass'}</Text>
            </View>
          )}
        </View>

        {/* 6: Assessment & Verdict */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Overall Statutory Safety Assessment</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Installation Verdict:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold', color: '#9a3412' }]}>{sign.overallResult || 'SAFE TO USE'}</Text>
          </View>
          <Text style={{ fontSize: 6.5, color: '#334155', marginTop: 2 }}>
            {sign.defectsNotes || 'Mobile catering LPG installation tested in full compliance with UKLPG Code of Practice 24.'}
          </Text>
          <View style={[styles.fieldRow, { marginTop: 3 }]}>
            <Text style={styles.fieldLabel}>Next Inspection Due:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold' }]}>{sign.nextInspectionDueDate || '12 Months'}</Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Gas Safe Registered Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{eng.engineerName || 'Gas Safe Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Gas Safe LPG Mobile Catering Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{owner.inspectionDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Catering Vehicle Operator</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{owner.operatorName || 'Vehicle Operator'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Safety certificate received</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{owner.inspectionDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            UKLPG Code of Practice 24 • Statutory Mobile Catering Vehicle Gas Safety Record
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default MobileCateringGasSafetyRecordPDF;
